import React, { useEffect, useState } from "react";
import { componenteImgSrc } from "@/lib/cloudinary";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import DetalleMontaje from "@/components/DetalleMontaje";
import { DestinoHeader, DestinosResumen, destinoDeParada } from "@/components/DestinoHeader";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  LogOut, MapPin, Phone, Clock, Calendar, Package, ChevronRight,
  CheckCircle2, AlertTriangle, Camera, Loader2, ExternalLink, Paperclip,
  FileText, Image as ImgIcon, File as FileIcon, ArrowLeft, ScanLine, Receipt,
  CheckSquare, Clock4, Video as VideoIcon, Play, PackageCheck, Palmtree,
} from "lucide-react";
import { uploadToCloudinary, cloudinaryThumb } from "@/lib/cloudinary";
import { toast } from "sonner";
import { NoRealizadaSheet, ResolverParadasModal } from "@/components/NoRealizada";
import DnfLogo from "@/components/DnfLogo";
import ProyectoThumb from "@/components/ProyectoThumb";
import ParteVehiculoBlock from "@/components/ParteVehiculoBlock";
import CargaRuta from "@/pages/CargaRuta";
import QuickGastoModal from "@/components/QuickGastoModal";
import LearnerHint from "@/components/LearnerHint";
import RutaMapa from "@/components/RutaMapa";
import { RequisitosDetalle, RequisitosChips } from "@/components/espacios/RequisitosForm";
import { MotivoEvidenciaPicker, motivoValido, motivoLabel } from "@/components/MotivoEvidenciaPicker";
import { PermisoCorreoBadge, PermisoCorreoBlock } from "@/components/PermisoCorreo";
import { MenuEmpleadoButton, useCapacidadesRRHH } from "@/components/MenuEmpleado";
import { MiniResumenRRHH } from "@/components/MiniResumenRRHH";

const ESTADO_PARADA = {
  pendiente:   { label: "Pendiente",   color: "bg-zinc-700/60 text-zinc-200", icon: Clock },
  iniciada:    { label: "En curso",    color: "bg-blue-500/30 text-blue-100", icon: Clock },
  finalizada:  { label: "Finalizada",  color: "bg-emerald-500/30 text-emerald-100", icon: CheckCircle2 },
  incidencia:  { label: "Incidencia",  color: "bg-red-500/30 text-red-100", icon: AlertTriangle },
  no_realizada:{ label: "No realizada", color: "bg-red-900/50 text-red-200", icon: AlertTriangle },
};

function fechaCorta(iso) {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
}

function esHoy(iso) {
  const t = new Date().toISOString().slice(0, 10);
  return iso === t;
}

function MobileShell({ children, title, onBack }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0c] text-zinc-100">
      <header className="sticky top-0 z-30 bg-[#0c0c10] border-b border-zinc-800 px-3 py-3 flex items-center gap-2">
        {onBack ? (
          <button onClick={onBack} className="p-1.5 rounded hover:bg-zinc-900" data-testid="mobile-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <DnfLogo size={36} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-amber-400 truncate">DNF · Montaje</div>
          <div className="text-sm font-semibold truncate">{title || user?.name || "Mis rutas"}</div>
        </div>
        <MenuEmpleadoButton />
        <button onClick={async () => { await logout(); nav("/login"); }}
          className="p-1.5 rounded hover:bg-red-500/20 text-zinc-400 hover:text-red-300"
          data-testid="mobile-logout" aria-label="Salir" title="Salir">
          <LogOut className="w-5 h-5" />
        </button>
      </header>
      <main className="max-w-2xl mx-auto pb-24">{children}</main>
    </div>
  );
}

export default function MiRuta() {
  const [rutas, setRutas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // ruta id
  const [paradaOpen, setParadaOpen] = useState(null); // idx
  const [cargaOpen, setCargaOpen] = useState(false);
  const [tareasPend, setTareasPend] = useState(0);
  const nav = useNavigate();
  const { hasPermission } = useAuth();
  const caps = useCapacidadesRRHH();
  const portalInterno = caps?.tipo === "interno" && (caps.banco_horas || caps.ausencias);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mis-rutas");
      setRutas(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/tareas/mias-pendientes").then(({ data }) => setTareasPend(data.count || 0)).catch(() => {});
  }, []);

  if (selected) {
    const ruta = rutas.find((r) => r.id === selected);
    if (!ruta) { setSelected(null); return null; }
    if (cargaOpen) {
      return <CargaRuta rutaId={selected} onBack={() => setCargaOpen(false)} />;
    }
    if (paradaOpen !== null) {
      return (
        <ParadaDetalle
          ruta={ruta}
          idx={paradaOpen}
          onBack={() => setParadaOpen(null)}
          onChange={load}
        />
      );
    }
    return (
      <RutaDetalle
        ruta={ruta}
        onBack={() => setSelected(null)}
        onAbrirParada={(idx) => setParadaOpen(idx)}
        onAbrirCarga={() => setCargaOpen(true)}
        onReload={load}
      />
    );
  }

  return (
    <MobileShell>
      <div className="p-4">
        <h1 className="text-lg font-bold mb-1">Mis rutas asignadas</h1>
        <p className="text-xs text-zinc-500 mb-4">Últimos 14 días + próximos 14 días</p>

        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center justify-between" data-testid="accesos-rapidos-title">
          <span>Accesos rápidos</span>
          <span className="normal-case text-zinc-600">Más opciones en <b className="text-zinc-400">Menú</b> ↑</span>
        </div>
        {/* Acceso a Mis Tareas */}
        <button
          onClick={() => nav("/tareas")}
          data-testid="mobile-mis-tareas"
          className="w-full bg-zinc-900/60 hover:bg-zinc-900 border border-amber-500/40 rounded-xl p-3 mb-2 flex items-center gap-3 transition-colors text-left">
          <CheckSquare className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-zinc-100">Mis tareas</div>
            <div className="text-[11px] text-zinc-500">
              {tareasPend > 0 ? `${tareasPend} pendiente(s)` : "Sin tareas pendientes"}
            </div>
          </div>
          {tareasPend > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
              {tareasPend}
            </span>
          )}
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </button>

        {/* Acceso al Fichaje */}
        <button
          onClick={() => nav("/fichaje")}
          data-testid="mobile-fichaje"
          className="w-full bg-zinc-900/60 hover:bg-zinc-900 border border-emerald-500/40 rounded-xl p-3 mb-4 flex items-center gap-3 transition-colors text-left">
          <Clock4 className="w-5 h-5 text-emerald-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-zinc-100">Fichaje horario</div>
            <div className="text-[11px] text-zinc-500">Entrada / salida con GPS</div>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-500" />
        </button>

        {/* Portal del empleado · sólo personal interno (rrhh_capacidades) */}
        {portalInterno && (
          <button
            onClick={() => nav("/rrhh")}
            data-testid="mobile-portal-empleado"
            className="w-full bg-zinc-900/60 hover:bg-zinc-900 border border-indigo-500/40 rounded-xl p-3 -mt-2 mb-4 flex items-center gap-3 transition-colors text-left">
            <Palmtree className="w-5 h-5 text-indigo-300 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-100">Portal del empleado</div>
              <div className="text-[11px] text-zinc-500">Horas · fichajes · solicitudes · documentos</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </button>
        )}

        {/* Acceso a Recepción de material (permiso logistica:recepcion) */}
        {hasPermission("logistica:recepcion") && (
          <button
            onClick={() => nav("/logistica-entrada")}
            data-testid="mobile-recepcion-material"
            className="w-full bg-zinc-900/60 hover:bg-zinc-900 border border-blue-500/40 rounded-xl p-3 -mt-2 mb-4 flex items-center gap-3 transition-colors text-left">
            <PackageCheck className="w-5 h-5 text-blue-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-zinc-100">Recepción de material</div>
              <div className="text-[11px] text-zinc-500">Marcar recibido e imprimir etiqueta</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-500" />
          </button>
        )}

        {/* Mini resumen RRHH · sólo personal interno (lee /portal-empleado/resumen) */}
        {portalInterno && <MiniResumenRRHH />}

        {loading && (
          <div className="text-center py-12 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        )}

        {!loading && rutas.length === 0 && (
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-8 text-center">
            <Package className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
            <div className="text-sm text-zinc-400">No tienes rutas asignadas</div>
            <div className="text-xs text-zinc-500 mt-1">Cuando te asignen una, aparecerá aquí.</div>
          </div>
        )}

        {rutas.some((r) => r.estado === "en_curso") && (
          <div className="bg-amber-500/10 border border-amber-500/50 rounded-xl p-3 text-sm text-amber-100" data-testid="ruta-activa-banner">
            🚧 Tienes una ruta <b>en curso</b>: <b>{rutas.find((r) => r.estado === "en_curso")?.nombre}</b>. Finalízala (todas las paradas con resultado) antes de empezar otra.
            <button onClick={() => setSelected(rutas.find((r) => r.estado === "en_curso")?.id)} data-testid="ir-ruta-activa" className="block mt-2 underline font-semibold">Ir a la ruta pendiente →</button>
          </div>
        )}
        <div className="space-y-3" data-testid="mis-rutas-list">
          {rutas.map((r) => { const bloqueada = rutas.some((x) => x.estado === "en_curso" && x.id !== r.id) && r.estado !== "en_curso" && !r.autorizacion_inicio_paralelo; return (
            <button key={r.id} onClick={() => bloqueada ? toast.error("Termina primero la ruta en curso") : setSelected(r.id)}
              data-testid={`mi-ruta-${r.codigo}`} aria-disabled={bloqueada}
              className={`w-full text-left bg-zinc-900/60 border rounded-xl p-4 transition-colors ${bloqueada ? "border-zinc-800 opacity-50" : "border-zinc-800 hover:border-amber-500/50"}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-amber-400" />
                  <span className={`text-sm font-semibold ${esHoy(r.fecha) ? "text-amber-300" : "text-zinc-200"}`}>
                    {fechaCorta(r.fecha)}
                  </span>
                  {esHoy(r.fecha) && <span className="text-[9px] uppercase bg-amber-500 text-zinc-900 font-bold px-1.5 py-0.5 rounded">HOY</span>}
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              </div>
              <div className="text-base font-semibold mt-0.5">{r.nombre}</div>
              <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-zinc-400">
                {r.hora_salida && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {r.hora_salida}</span>}
                {r.provincia_principal && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {r.provincia_principal}</span>}
                <span className="flex items-center gap-0.5"><Package className="w-3 h-3" /> {r.paradas?.length || 0} parada(s)</span>
                {r.estado === "en_curso" && <span className="text-amber-300 font-semibold">● en curso</span>}
                {bloqueada && <span className="text-zinc-500">🔒 bloqueada</span>}
              </div>
            </button>
          ); })}
        </div>
      </div>
    </MobileShell>
  );
}

function RutaDetalle({ ruta, onBack, onAbrirParada, onAbrirCarga, onReload }) {
  const [gastoOpen, setGastoOpen] = useState(false);
  return (
    <MobileShell title={ruta.nombre} onBack={onBack}>
      <div className="p-4 space-y-4">
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Ruta</div>
          <div className="text-base font-semibold">{ruta.nombre}</div>
          <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-2">
            <span className="flex items-center gap-0.5"><Calendar className="w-3 h-3" />{fechaCorta(ruta.fecha)}</span>
            {ruta.hora_salida && <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />Salida {ruta.hora_salida}</span>}
            {ruta.provincia_principal && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{ruta.provincia_principal}</span>}
          </div>
          {(ruta.montadores || []).length > 0 && (
            <div className="mt-2 pt-2 border-t border-zinc-800 text-xs">
              <div className="text-zinc-500 mb-0.5">Equipo:</div>
              <div className="flex flex-wrap gap-1.5">
                {ruta.montadores.map((m, i) => (
                  <span key={i} className="bg-zinc-950 border border-zinc-800 rounded px-2 py-0.5">{m.nombre_visible} <span className="text-zinc-500">· {m.rol}</span></span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Parte de vehículo (jornada del montador) — solo si NO es vehículo propio */}
        {ruta.vehiculo_propio ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200 flex items-center gap-2" data-testid="vehiculo-propio-aviso">
            <span className="text-base">🚐</span>
            <div>
              <div className="font-semibold">Vehículo propio</div>
              <div className="text-xs text-amber-200/70">No se requiere parte de vehículo para esta ruta.</div>
            </div>
          </div>
        ) : (
          <ParteVehiculoBlock
            rutaId={ruta.id}
            vehiculoId={ruta.vehiculo_id}
            montadores={ruta.montadores || []}
            onChange={onReload}
            compact
          />
        )}

        {/* Botón Descargar Parte de Trabajo PDF */}
        <button
          onClick={async () => {
            try {
              const r = await api.get(`/rutas/${ruta.id}/parte.pdf`, { responseType: "blob" });
              const blob = r.data;
              const url = URL.createObjectURL(blob);
              // En móviles, abrir en nueva pestaña funciona mejor que descargar
              const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
              const a = document.createElement("a");
              a.href = url;
              if (isMobile) {
                a.target = "_blank";
                a.rel = "noreferrer";
              } else {
                a.download = `parte_${ruta.nombre || ruta.id}.pdf`;
              }
              document.body.appendChild(a);
              a.click();
              setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 2000);
            } catch (e) {
              toast.error(`No se pudo descargar el parte (${e?.response?.status || e?.message || "error"})`);
            }
          }}
          data-testid="descargar-parte-pdf-btn"
          className="w-full bg-zinc-900/60 hover:bg-zinc-900 border border-amber-400/40 text-amber-300 font-semibold rounded-xl p-3 flex items-center justify-center gap-2 transition-colors">
          <FileText className="w-5 h-5" />
          Descargar parte de trabajo (PDF)
        </button>

        {/* Botón Cargar furgo (QR) */}
        {onAbrirCarga && (
          <button
            onClick={onAbrirCarga}
            data-testid="abrir-carga-btn"
            className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 font-semibold rounded-xl p-3 flex items-center justify-center gap-2 transition-colors">
            <ScanLine className="w-5 h-5" />
            Cargar furgo (escanear QR)
            {ruta.carga_completada_at && <CheckCircle2 className="w-4 h-4 ml-1" />}
          </button>
        )}

        {/* Botón Añadir gasto a la ruta */}
        <button
          onClick={() => setGastoOpen(true)}
          data-testid="abrir-gasto-btn"
          className="w-full bg-zinc-900/60 hover:bg-zinc-900 border border-sky-500/40 text-sky-300 font-semibold rounded-xl p-3 flex items-center justify-center gap-2 transition-colors">
          <Receipt className="w-5 h-5" />
          Añadir gasto a esta ruta
        </button>
        <QuickGastoModal
          open={gastoOpen}
          onOpenChange={setGastoOpen}
          rutaId={ruta.id}
          title={`Gasto · ${ruta.nombre}`}
        />

        {ruta.observaciones && (
          <div className="bg-amber-500/10 border-l-4 border-amber-500/60 rounded p-3 text-sm">
            <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">Instrucciones generales</div>
            <div className="text-zinc-200 whitespace-pre-wrap">{ruta.observaciones}</div>
          </div>
        )}

        {/* 🗺️ Mapa de la ruta con todos los puntos numerados */}
        {(ruta.paradas || []).length > 0 && (
          <div data-testid="mi-ruta-mapa">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1.5">
              <MapPin className="w-3 h-3" /> Mapa de la ruta
            </div>
            <RutaMapa rutaId={ruta.id} height={260} />
          </div>
        )}

        {/* Adjuntos GLOBALES de la ruta (los específicos por parada se ven al abrir el trabajo) */}
        {(() => {
          const adjGlobal = (ruta.adjuntos || []).filter((a) => !a.linea_id);
          if (adjGlobal.length === 0) return null;
          return (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid="ruta-adjuntos-globales">
              <div className="text-xs font-semibold flex items-center gap-1.5 mb-2">
                <Paperclip className="w-3.5 h-3.5 text-amber-400" /> Documentos generales ({adjGlobal.length})
                <span className="text-[10px] text-zinc-500 font-normal ml-auto">Aplican a toda la ruta</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {adjGlobal.map((adj) => {
                  const isImg = (adj.tipo || "").startsWith("image/");
                  const Ico = isImg ? ImgIcon : adj.tipo === "application/pdf" ? FileText : FileIcon;
                  return (
                    <a key={adj.id} href={adj.url} target="_blank" rel="noreferrer"
                      className="bg-zinc-950 border border-zinc-800 rounded overflow-hidden">
                      {isImg ? (
                        <img src={cloudinaryThumb(adj.url, 200, 130)} alt={adj.nombre} className="w-full h-20 object-cover" />
                      ) : (
                        <div className="h-20 flex items-center justify-center text-zinc-400"><Ico className="w-8 h-8" /></div>
                      )}
                      <div className="text-[10px] p-1 truncate text-zinc-300">{adj.nombre}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <div className="text-xs uppercase tracking-wider text-zinc-500 px-1 flex items-center gap-2">Paradas ({(ruta.paradas || []).length}) <DestinosResumen ruta={ruta} /></div>
        <div className="space-y-2" data-testid="paradas-list-mobile">
          {(ruta.paradas || []).map((p, idx) => {
            const est = ESTADO_PARADA[p.estado] || ESTADO_PARADA.pendiente;
            const EstIco = est.icon;
            const dir = [p.espacio?.direccion, p.espacio?.cp, p.espacio?.ciudad].filter(Boolean).join(", ");
            const comps = (p.componentes || []).filter((c) => !c.parent_id);
            const docsParada = (ruta.adjuntos || []).filter((a) => a.linea_id === p.linea_id).length;
            return (
              <React.Fragment key={idx}>
              <DestinoHeader destino={destinoDeParada(ruta.destinos, idx)} compacto />
              <button onClick={() => onAbrirParada(idx)}
                data-testid={`parada-${idx}`}
                className="w-full text-left bg-zinc-900/60 border border-zinc-800 hover:border-amber-500/50 rounded-xl p-3 transition-colors">
                <div className="flex items-start gap-3">
                  <span className="bg-amber-500 text-zinc-900 font-bold text-sm w-8 h-8 rounded-full flex items-center justify-center shrink-0">{p.orden}</span>
                  <ProyectoThumb url={p.linea?.imagen_url || p.peticion?.imagen_url}
                    nombre={p.cliente_nombre || p.espacio?.nombre || "?"} size={42} rounded="lg"
                    data-testid={`parada-${idx}-thumb`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="text-base font-semibold truncate">{p.espacio?.nombre || p.manual_titulo || "—"}</span>
                      {p.tarea_interna && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-cyan-500/25 text-cyan-200 border border-cyan-500/40 font-bold">
                          🔧 INTERNA
                        </span>
                      )}
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded flex items-center gap-0.5 ${est.color}`}>
                        <EstIco className="w-2.5 h-2.5" /> {est.label}
                      </span>
                      {docsParada > 0 && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded flex items-center gap-0.5 bg-cyan-500/25 text-cyan-200"
                          data-testid={`parada-${idx}-docs-badge`}>
                          <Paperclip className="w-2.5 h-2.5" /> {docsParada}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-zinc-400 flex items-center gap-1"><MapPin className="w-3 h-3" /> {dir || "—"}</div>
                    {p.hora_estimada && <div className="text-xs text-amber-300 mt-0.5">🕒 {p.hora_estimada}</div>}
                    <div className="mt-0.5"><PermisoCorreoBadge linea={p.linea} testId={`parada-${idx}-permiso-badge`} /></div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {comps.length} componente(s)
                      {(p.componentes_no_incluidos_ids?.length || 0) > 0 && (
                        <span className="ml-2 inline-block bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5 text-[10px]" data-testid={`parada-${idx}-pendientes-badge`}>
                          +{p.componentes_no_incluidos_ids.length} pdte. oficina
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-500 mt-1" />
                </div>
              </button>
              </React.Fragment>
            );
          })}
        </div>

        {/* FINALIZAR RUTA — check de pendientes */}
        <FinalizarRutaBtn ruta={ruta} onBack={onBack} onAbrirParada={onAbrirParada} onReload={onReload} />
      </div>
    </MobileShell>
  );
}

function FinalizarRutaBtn({ ruta, onBack, onAbrirParada, onReload }) {
  const [working, setWorking] = useState(false);
  const [resolver, setResolver] = useState(false);
  if (ruta.estado === "finalizada") {
    return (
      <div className="mt-4 bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4 text-center text-emerald-200 text-sm font-semibold"
           data-testid="ruta-finalizada-banner">
        ✅ Ruta finalizada · ¡Buen trabajo!
      </div>
    );
  }
  const paradas = ruta.paradas || [];
  const pendientesList = paradas.map((p, idx) => ({ p, idx })).filter(({ p }) => !p.estado_cierre);
  const finalizar = async () => {
    if (pendientesList.length > 0) { setResolver(true); return; }
    if (!window.confirm("¿Finalizar la ruta? Todas las paradas tienen resultado.")) return;
    setWorking(true);
    try {
      await api.post(`/rutas/${ruta.id}/finalizar`, {});
      toast.success("Ruta finalizada · ¡Perfecto!");
      onBack && onBack();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "paradas_sin_resultado") { toast.error(d.mensaje); onReload?.(); setResolver(true); }
      else toast.error(typeof d === "string" ? d : "Error finalizando ruta");
    } finally { setWorking(false); }
  };
  const pendientes = pendientesList.length;
  return (
    <>
    <LearnerHint label="Cerrar la ruta cuando todas las paradas tengan resultado">
      <button onClick={finalizar} disabled={working}
        data-testid="btn-finalizar-ruta"
        className={`w-full mt-4 rounded-xl p-4 font-semibold flex items-center justify-center gap-2 transition-colors ${
          pendientes > 0
            ? "bg-amber-500/15 border-2 border-amber-500/60 text-amber-200 hover:bg-amber-500/25"
            : "bg-emerald-500 hover:bg-emerald-600 text-zinc-900"
        }`}>
        {working ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
        {pendientes > 0 ? `Finalizar ruta · resolver ${pendientes} parada${pendientes!==1?'s':''}` : "Finalizar ruta · todo OK"}
      </button>
    </LearnerHint>
    {resolver && <ResolverParadasModal ruta={ruta} pendientes={pendientesList} onClose={() => setResolver(false)}
      onAbrirParada={(idx) => { setResolver(false); onAbrirParada?.(idx); }} onChange={() => { onReload?.(); }} />}
    </>
  );
}

function ParadaDetalle({ ruta, idx, onBack, onChange }) {
  const navigate = useNavigate();
  const p = ruta.paradas[idx] || {};
  const paradaExiste = !!ruta.paradas[idx];
  const dir = [p.espacio?.direccion, p.espacio?.cp, p.espacio?.ciudad].filter(Boolean).join(", ");
  const maps = dir ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}` : null;
  const comps = (p.componentes || []).filter((c) => !c.parent_id);
  const yaIniciada = !!p.iniciado_at || p.estado === "iniciada" || p.estado === "finalizada" || p.estado === "incidencia";
  const yaCerrada = !!p.estado_cierre;
  const [showNoRealizada, setShowNoRealizada] = useState(false);
  const docsParada = (ruta.adjuntos || []).filter((a) => a.linea_id === p.linea_id);

  // Toast informativo al abrir la parada si tiene documentos específicos.
  // Solo se muestra una vez por parada por sesión del navegador.
  useEffect(() => {
    if (docsParada.length === 0) return;
    const seenKey = `dnf_docs_seen_${ruta.id}_${p.linea_id}`;
    if (typeof window === "undefined" || !window.sessionStorage) return;
    if (window.sessionStorage.getItem(seenKey)) return;
    window.sessionStorage.setItem(seenKey, "1");
    toast.info(
      `📎 ${docsParada.length} documento${docsParada.length !== 1 ? "s" : ""} para este trabajo — desliza para revisarlo${docsParada.length !== 1 ? "s" : ""}`,
      { duration: 4500 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruta.id, p.linea_id, docsParada.length]);

  const [fotoPrevia, setFotoPrevia] = useState(p.foto_previa || null);
  const [fotosFinales, setFotosFinales] = useState(p.fotos_finales || []);
  const [uploading, setUploading] = useState(null); // "previa" | "final"

  const [showCierre, setShowCierre] = useState(false);
  const [estadoCierre, setEstadoCierre] = useState(p.estado_cierre || "");
  const [tipoInc, setTipoInc] = useState(p.tipo_incidencia || "");
  const [descInc, setDescInc] = useState(p.incidencia_descripcion || "");
  // Iter 84.6 — Persona contactada in-situ (obligatorio en incidencia, opcional cargo).
  const [contactoNombre, setContactoNombre] = useState(p.incidencia_contacto_nombre || "");
  const [contactoCargo, setContactoCargo] = useState(p.incidencia_contacto_cargo || "");
  const [firmaNombre, setFirmaNombre] = useState(p.cliente_firma_nombre || "");
  const [saving, setSaving] = useState(false);
  // Fase B · excepciones de evidencia (sin aprobación de oficina, pero trazadas)
  const [fotoPreviaOmitida, setFotoPreviaOmitida] = useState(p.foto_previa_omitida || null);
  const [showOmitirPrevia, setShowOmitirPrevia] = useState(false);
  const [omitirMotivo, setOmitirMotivo] = useState("");
  const [omitirDetalle, setOmitirDetalle] = useState("");
  const [evidenciaIncompleta, setEvidenciaIncompleta] = useState(false);
  const [eviMotivo, setEviMotivo] = useState("");
  const [eviDetalle, setEviDetalle] = useState("");

  // Iter 111 — La parada puede haber sido eliminada (edición con PIN) mientras
  // el montador la tenía abierta → evitar crash "undefined is not an object".
  if (!paradaExiste) {
    return (
      <div className="p-4 space-y-4" data-testid="parada-eliminada-aviso">
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 text-sm text-amber-200">
          ⚠️ Esta parada ya no está en la ruta (ha sido modificada desde oficina).
        </div>
        <button onClick={onBack}
          className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold py-3 rounded-lg">
          ← Volver a la ruta
        </button>
      </div>
    );
  }

  // Geolocalización del navegador
  const getCoords = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ latitud: null, longitud: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitud: pos.coords.latitude, longitud: pos.coords.longitude }),
      () => resolve({ latitud: null, longitud: null }),
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
    );
  });

  const subirFoto = async (file, tipo, categoria = "libre") => {
    if (!file) return;
    setUploading(tipo);
    try {
      const coords = await getCoords();
      // C1 — Si la petición de esta parada tiene geolocalizar_fotos !== false, estampar
      let fileToUpload = file;
      // Iter 81 — Comprimir SOLO fotos finales (Floren Decor Galva: AAFF mantiene
      // calidad por ser pre-producción). previa también se comprime — es solo
      // referencia, no AAFF.
      if (file.type && file.type.startsWith("image/")) {
        try {
          const { compressImage } = await import("@/lib/compressImage");
          fileToUpload = await compressImage(file, { maxSide: 1600, quality: 0.8 });
        } catch (_e) { fileToUpload = file; }
      }
      const geoOn = p?.peticion?.geolocalizar_fotos !== false;
      if (geoOn) {
        try {
          const { stampPhotoWithGeo } = await import("@/lib/photoGeo");
          // Jun-2026: sello con la ubicación del ESPACIO (siempre correcta) y sin fecha
          const espDir = [p.espacio?.direccion, p.espacio?.cp, p.espacio?.ciudad].filter(Boolean).join(", ") || null;
          const espCoords = (p.espacio?.latitud != null && p.espacio?.longitud != null)
            ? { latitud: p.espacio.latitud, longitud: p.espacio.longitud }
            : (coords.latitud != null ? coords : null);
          fileToUpload = await stampPhotoWithGeo(fileToUpload, { direccion: espDir, coords: espCoords });
        } catch (_e) { /* fallback al archivo original */ }
      }
      const up = await uploadToCloudinary(fileToUpload, "dnf/montajes", "image");
      const endpoint = tipo === "previa"
        ? `/rutas/${ruta.id}/paradas/${idx}/foto-previa`
        : `/rutas/${ruta.id}/paradas/${idx}/fotos-finales`;
      const { data } = await api.post(endpoint, {
        url: up.secure_url,
        public_id: up.public_id,
        categoria,
        tipo: "foto",
        mime: file.type,
        size_bytes: up.bytes ?? file.size,
        latitud: coords.latitud,
        longitud: coords.longitud,
      });
      if (tipo === "previa") setFotoPrevia(data);
      else setFotosFinales((arr) => [...arr, data]);
      toast.success("Foto subida");
      onChange && (await onChange());
    } catch (err) {
      toast.error(err.message || "Error subiendo foto");
    } finally { setUploading(null); }
  };

  // ====== Subida de VÍDEO (mp4/mov/webm) ======
  // Por defecto se sube como foto_final con categoria=libre. Límite 50 MB y formato whitelist.
  const VIDEO_MAX_MB = 50;
  const VIDEO_MAX_BYTES = VIDEO_MAX_MB * 1024 * 1024;
  const VIDEO_MIME_WHITELIST = ["video/mp4", "video/quicktime", "video/webm", "video/mpeg"];

  const subirVideo = async (file, tipo = "final", categoria = "libre") => {
    if (!file) return;
    // Validación cliente — Cloudinary también validará por el plan, pero esto es UX
    const mime = (file.type || "").toLowerCase();
    const okMime = VIDEO_MIME_WHITELIST.some((m) => mime.startsWith(m)) ||
                   /\.(mp4|mov|webm|m4v)$/i.test(file.name || "");
    if (!okMime) {
      toast.error("Formato no soportado · usa MP4, MOV o WebM");
      return;
    }
    if (file.size > VIDEO_MAX_BYTES) {
      toast.error(`El vídeo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB · máx ${VIDEO_MAX_MB} MB`);
      return;
    }
    setUploading(tipo === "previa" ? "previa-video" : "final-video");
    try {
      const coords = await getCoords();
      const up = await uploadToCloudinary(file, "dnf/montajes", "video");
      // Thumbnail/poster: Cloudinary genera el frame 0 al cambiar .mp4 por .jpg en la URL.
      let thumb = null;
      try {
        thumb = (up.secure_url || "").replace(/\.(mp4|mov|webm|m4v)(\?.*)?$/i, ".jpg");
        if (thumb === up.secure_url) thumb = null;
      } catch (_e) { thumb = null; }
      const endpoint = tipo === "previa"
        ? `/rutas/${ruta.id}/paradas/${idx}/foto-previa`
        : `/rutas/${ruta.id}/paradas/${idx}/fotos-finales`;
      const { data } = await api.post(endpoint, {
        url: up.secure_url,
        public_id: up.public_id,
        categoria,
        tipo: "video",
        mime: file.type,
        size_bytes: up.bytes ?? file.size,
        thumbnail_url: thumb,
        latitud: coords.latitud,
        longitud: coords.longitud,
      });
      if (tipo === "previa") setFotoPrevia(data);
      else setFotosFinales((arr) => [...arr, data]);
      toast.success("Vídeo subido");
      onChange && (await onChange());
    } catch (err) {
      toast.error(err.message || "Error subiendo vídeo");
    } finally { setUploading(null); }
  };

  // ====== Subida MÚLTIPLE: el montador elige varias fotos de su galería ======
  const subirFotosMultiples = async (files) => {
    if (!files || files.length === 0) return;
    setUploading("final");
    let ok = 0; let ko = 0;
    try {
      const coords = await getCoords();
      const geoOn = p?.peticion?.geolocalizar_fotos !== false;
      const { stampPhotoWithGeo } = geoOn ? await import("@/lib/photoGeo") : { stampPhotoWithGeo: null };
      // Subir secuencial (más estable para móvil con conexión irregular)
      const { compressImage } = await import("@/lib/compressImage");
      for (const file of files) {
        try {
          let fileToUpload = file;
          // Iter 81 — comprimir antes de geo + subida para reducir peso
          if (file.type && file.type.startsWith("image/")) {
            try { fileToUpload = await compressImage(file, { maxSide: 1600, quality: 0.8 }); } catch (_e) { fileToUpload = file; }
          }
          if (stampPhotoWithGeo) {
            try {
              // Jun-2026: sello con la ubicación del ESPACIO (siempre correcta) y sin fecha
              const espDir = [p.espacio?.direccion, p.espacio?.cp, p.espacio?.ciudad].filter(Boolean).join(", ") || null;
              const espCoords = (p.espacio?.latitud != null && p.espacio?.longitud != null)
                ? { latitud: p.espacio.latitud, longitud: p.espacio.longitud }
                : (coords.latitud != null ? coords : null);
              fileToUpload = await stampPhotoWithGeo(fileToUpload, { direccion: espDir, coords: espCoords });
            } catch (_e) { /* fallback al original */ }
          }
          const up = await uploadToCloudinary(fileToUpload, "dnf/montajes", "image");
          const { data } = await api.post(
            `/rutas/${ruta.id}/paradas/${idx}/fotos-finales`,
            {
              url: up.secure_url,
              public_id: up.public_id,
              categoria: "libre",
              latitud: coords.latitud,
              longitud: coords.longitud,
            },
          );
          setFotosFinales((arr) => [...arr, data]);
          ok += 1;
        } catch (e) {  // eslint-disable-line no-unused-vars
          ko += 1;
        }
      }
      if (ok > 0) toast.success(`${ok} foto${ok!==1?'s':''} subida${ok!==1?'s':''}${ko > 0 ? ` · ${ko} fallaron` : ""}`);
      else toast.error("No se pudieron subir las fotos");
      onChange && (await onChange());
    } finally { setUploading(null); }
  };

  const quitarFoto = async (foto) => {
    if (!window.confirm("¿Quitar esta foto?")) return;
    try {
      await api.delete(`/rutas/${ruta.id}/paradas/${idx}/fotos/${foto.id}`);
      if (foto.categoria === "previa") setFotoPrevia(null);
      else setFotosFinales((arr) => arr.filter((f) => f.id !== foto.id));
      onChange && (await onChange());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al quitar");
    }
  };

  const iniciar = async () => {
    setSaving(true);
    try {
      await api.post(`/rutas/${ruta.id}/paradas/${idx}/iniciar`);
      toast.success("Montaje iniciado");
      onChange && (await onChange());
    } catch (err) {
      const det = err.response?.data?.detail;
      if (det && typeof det === "object" && det.error === "carga_incompleta") {
        toast.error(det.mensaje || "Carga incompleta. Ve a Cargar ruta.", {
          action: {
            label: "Cargar ruta",
            onClick: () => navigate(`/mi-ruta/${ruta.id}/cargar`),
          },
          duration: 10000,
        });
      } else if (det && typeof det === "object" && det.code === "ruta_activa_pendiente") {
        toast.error(det.mensaje, { duration: 10000 });
      } else {
        toast.error(typeof det === "string" ? det : det?.mensaje || "Error");
      }
    } finally { setSaving(false); }
  };

  const omitirFotoPrevia = async () => {
    const err = motivoValido(omitirMotivo, omitirDetalle);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const r = await api.post(`/rutas/${ruta.id}/paradas/${idx}/omitir-foto-previa`, { motivo: omitirMotivo, detalle: omitirDetalle.trim() || null });
      setFotoPreviaOmitida(r.data.foto_previa_omitida);
      setShowOmitirPrevia(false);
      toast.success("Registrado: sin foto previa (queda trazado en el reporte)");
      onChange && (await onChange());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar");
    } finally { setSaving(false); }
  };

  const cerrar = async () => {
    if (!estadoCierre) return toast.error("Elige cómo cierras el montaje");
    if (["semi_montado", "incidencia"].includes(estadoCierre)) {
      if (!tipoInc) return toast.error("Elige el tipo de incidencia");
      if (!descInc.trim()) return toast.error("Describe la incidencia");
    }
    // Iter 84.6 — para incidencia, persona contactada obligatoria.
    if (estadoCierre === "incidencia" && !contactoNombre.trim()) {
      return toast.error("Indica con quién has hablado (persona contactada)");
    }
    const faltanFotos = ["montado", "semi_montado"].includes(estadoCierre) && fotosFinales.length < 3;
    if (faltanFotos) {
      if (fotosFinales.length === 0) return toast.error("Mínimo 1 foto final antes de cerrar");
      if (!evidenciaIncompleta) return toast.error("Mínimo 3 fotos finales, o marca «Evidencia incompleta» con motivo");
      const err = motivoValido(eviMotivo, eviDetalle);
      if (err) return toast.error(err);
    }
    setSaving(true);
    try {
      const r = await api.post(`/rutas/${ruta.id}/paradas/${idx}/cerrar`, {
        estado_cierre: estadoCierre,
        tipo_incidencia: tipoInc || null,
        incidencia_descripcion: descInc || null,
        incidencia_contacto_nombre: contactoNombre.trim() || null,
        incidencia_contacto_cargo: contactoCargo.trim() || null,
        cliente_firma_nombre: firmaNombre || null,
        evidencia_incompleta: faltanFotos ? { motivo: eviMotivo, detalle: eviDetalle.trim() || null } : null,
      });
      // Iter 77 — broadcast cross-tab para que /reportes refresque al instante
      try { localStorage.setItem("dnf-reporte-actualizado", String(Date.now())); } catch (_e) { /* private mode */ }
      toast.success(r.data?.revisita_linea_id ? "Cerrado. 2ª visita generada." : "Reporte enviado a oficina");
      onChange && (await onChange());
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cerrar");
    } finally { setSaving(false); }
  };

  return (
    <MobileShell title={`#${p.orden} · ${p.espacio?.nombre || p.manual_titulo || "Parada"}`} onBack={onBack}>
      {/* Iter 125 · Tareas internas: UI simplificada, sin fotos ni reporte */}
      {p.tarea_interna ? (
        <TareaInternaView ruta={ruta} idx={idx} p={p} onBack={onBack} onChange={onChange} />
      ) : (
      <div className="p-4 space-y-4">
        {/* Estado actual */}
        {yaCerrada && p.estado_cierre === "no_realizada" && (
          <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 text-sm text-red-100" data-testid="parada-no-realizada-banner">
            ⛔ NO realizada · <b>{p.no_realizada?.motivo}</b>{p.no_realizada?.detalle ? ` · ${p.no_realizada.detalle}` : ""}
            <div className="text-[10px] text-red-200/70 mt-1">Oficina decidirá replanificación / 2ª visita. {p.decision_no_realizada ? `Decidido: ${p.decision_no_realizada.accion}` : "Pendiente de decisión."}</div>
          </div>
        )}
        {yaCerrada && p.estado_cierre !== "no_realizada" && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 text-sm">
            ✅ Reporte enviado a oficina. {p.validacion?.estado === "aprobado" ? "✓ Aprobado." : p.validacion?.estado === "devuelto" ? `⚠️ Devuelto: ${p.validacion?.notas || "revisar"}` : "Pendiente de validación."}
          </div>
        )}
        {yaCerrada && p.ampliacion_evidencias?.abierta && (
          <div className="bg-sky-500/10 border border-sky-500/50 rounded-xl p-3 space-y-2" data-testid="ampliacion-evidencias-panel">
            <div className="text-sm font-semibold text-sky-200">📸 Oficina pide más fotos de este montaje</div>
            {p.ampliacion_evidencias.nota && <div className="text-xs text-sky-100/80">{p.ampliacion_evidencias.nota}</div>}
            <div className="text-[11px] text-zinc-400">Solo puedes añadir fotos nuevas. Lo ya enviado no se modifica.</div>
            <label className="block">
              <input type="file" accept="image/*" multiple className="hidden" data-testid="ampliacion-fotos-input"
                onChange={async (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; for (const f of fs) await subirFoto(f, "final", "libre"); }} />
              <span className="inline-flex items-center justify-center gap-1.5 w-full bg-sky-500 hover:bg-sky-400 text-zinc-900 font-semibold text-sm px-3 py-2 rounded-lg cursor-pointer">
                <Camera className="w-4 h-4" /> Añadir más fotos
              </span>
            </label>
            <button type="button" data-testid="ampliacion-terminar-btn"
              onClick={async () => {
                try {
                  await api.post(`/rutas/${ruta.id}/paradas/${idx}/ampliacion-evidencias/cerrar`, {});
                  toast.success("Fotos enviadas. El reporte vuelve a estar pendiente de validar.");
                  onChange && (await onChange());
                } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
              }}
              className="w-full text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-100 px-3 py-2 rounded-lg">
              He terminado de añadir fotos
            </button>
          </div>
        )}

        {/* Dirección */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
          <div className="text-xs text-zinc-500 mb-1">📍 Dirección</div>
          <div className="text-sm font-medium">{dir || "—"}</div>
          {maps && (
            <a href={maps} target="_blank" rel="noreferrer" data-testid="llevame-btn"
              className="mt-2 inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-semibold text-sm px-3 py-2 rounded-lg w-full justify-center">
              <ExternalLink className="w-4 h-4" /> Llévame con Google Maps
            </a>
          )}
          {p.espacio?.contacto_local && (
            <div className="mt-2 pt-2 border-t border-zinc-800 text-sm">
              <div className="text-zinc-500 text-xs">Contacto en tienda</div>
              <div>{p.espacio.contacto_local}</div>
              {p.espacio.telefono_local && (
                <a href={`tel:${p.espacio.telefono_local}`} className="inline-flex items-center gap-1 text-cyan-400 mt-1 text-sm">
                  <Phone className="w-3.5 h-3.5" /> {p.espacio.telefono_local}
                </a>
              )}
            </div>
          )}
          {p.espacio?.horario && <div className="mt-1 text-xs text-zinc-400">🕗 {p.espacio.horario}</div>}
        </div>
        <PermisoCorreoBlock linea={p.linea} testId={`mi-ruta-permiso-${p.linea_id}`} />
        {p.linea?.requisitos_montaje && (
          <div className="bg-amber-500/5 border border-amber-500/30 rounded-xl p-3" data-testid="parada-requisitos-montaje">
            <div className="text-xs text-amber-200 font-semibold mb-2 flex items-center gap-2 flex-wrap">🛠 Instrucciones de montaje <RequisitosChips req={p.linea.requisitos_montaje} testId={`mi-ruta-req-chips-${p.linea_id}`} /></div>
            <RequisitosDetalle req={p.linea.requisitos_montaje} testId={`mi-ruta-req-${p.linea_id}`} />
          </div>
        )}

        {/* Chips visuales de componentes incluidos en esta parada (Iter 57) */}
        {comps.length > 0 && (
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid="parada-componentes-chips">
            <div className="text-xs text-zinc-500 mb-2 flex items-center justify-between">
              <span>📦 Llevas en esta parada ({comps.length})</span>
              {(p.componentes_no_incluidos_ids?.length || 0) > 0 && (
                <span className="text-amber-300 text-[10px] uppercase tracking-wider">
                  ⚠ {p.componentes_no_incluidos_ids.length} pendiente(s) oficina
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {comps.map((c) => {
                const codigo = String(c.id || "").slice(0, 6).toUpperCase();
                return (
                  <span
                    key={c.id}
                    className="inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-200 border border-emerald-500/30 rounded-full px-2.5 py-1 text-[11px]"
                    title={`Código: ${codigo} · ${c.nombre}${c.bulto_numero ? ` · 📦 Bulto ${c.bulto_numero}` : ""}`}
                    data-testid={`chip-comp-${c.id}`}
                  >
                    <span className="font-mono text-emerald-100/90 bg-emerald-500/20 px-1 rounded">{codigo}</span>
                    {c.bulto_numero && <span className="font-mono text-emerald-300/80">#{c.bulto_numero}</span>}
                    <span className="truncate max-w-[160px]">{c.nombre}</span>
                    {c.cantidad > 1 && <span className="font-bold">×{c.cantidad}</span>}
                  </span>
                );
              })}
            </div>
            {(p.componentes_no_incluidos_ids?.length || 0) > 0 && (
              <div className="mt-2 pt-2 border-t border-zinc-800 text-[11px] text-amber-200/90 leading-relaxed">
                La oficina ha dejado <b>{p.componentes_no_incluidos_ids.length}</b> componente(s) de esta línea
                <b> fuera</b> de tu parada — se montarán en otra fecha/ruta.
              </div>
            )}
          </div>
        )}

        {/* Instrucciones — Iter 71: prominentes + check obligatorio antes del PASO 1 */}
        {p.observaciones && (
          <ObservacionesBanner
            parada={p}
            rutaId={ruta.id}
            idx={idx}
            onAck={onChange}
          />
        )}

        {/* Jun-2026 — Memoria de montaje en PDF (orden de trabajo con QRs y componentes) */}
        {p.linea_id && !p.tarea_interna && (
          <Button
            variant="outline"
            data-testid="mi-ruta-memoria-montaje-btn"
            onClick={async () => {
              try {
                const r = await api.get(`/memoria-montaje/linea/${p.linea_id}/pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(r.data);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60000);
              } catch (e) { toast.error("No se pudo generar la memoria de montaje"); }
            }}
            className="w-full bg-transparent border-amber-600/40 text-amber-300 hover:bg-amber-500/10 h-9 text-xs"
          >
            📄 Memoria de montaje (PDF) — componentes, QRs e instrucciones
          </Button>
        )}

        {/* Documentos de este trabajo (adjuntos asociados a la linea_id de esta parada) */}
        {docsParada.length > 0 && (
          <div className="bg-zinc-900/60 border border-cyan-500/30 rounded-xl p-3" data-testid="parada-docs">
            <div className="text-xs font-semibold flex items-center gap-1.5 mb-2">
              <Paperclip className="w-3.5 h-3.5 text-cyan-300" /> Documentos de este trabajo ({docsParada.length})
              <span className="text-[10px] text-zinc-500 font-normal ml-auto">Específicos de esta parada</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {docsParada.map((adj) => {
                const isImg = (adj.tipo || "").startsWith("image/");
                const Ico = isImg ? ImgIcon : adj.tipo === "application/pdf" ? FileText : FileIcon;
                return (
                  <a key={adj.id} href={adj.url} target="_blank" rel="noreferrer"
                    data-testid={`parada-doc-${adj.id}`}
                    className="bg-zinc-950 border border-zinc-800 rounded overflow-hidden">
                    {isImg ? (
                      <img src={cloudinaryThumb(adj.url, 240, 160)} alt={adj.nombre} className="w-full h-24 object-cover" />
                    ) : (
                      <div className="h-24 flex items-center justify-center text-zinc-400"><Ico className="w-9 h-9" /></div>
                    )}
                    <div className="text-[10px] p-1 truncate text-zinc-300">{adj.nombre}</div>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Componentes — vista detallada para el montador (Iter 124) */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
          {p.detalle_montaje && !p.es_manual && (
            <div className="mb-2">
              <DetalleMontaje detalle={p.detalle_montaje} campana={p.peticion?.codigo} marca={p.peticion?.marca} compacto testId={`detalle-montaje-movil-${p.orden}`} />
            </div>
          )}
          <div className="text-xs font-semibold mb-2">📦 Componentes ({comps.length})</div>
          <div className="space-y-2">
            {comps.map((c) => (
              <ComponenteMontajeCard key={c.id} c={c} paradaComponentes={p.componentes || []} />
            ))}
          </div>
        </div>

        {!yaCerrada && !p.tarea_interna && (
          <button type="button" onClick={() => setShowNoRealizada(true)} data-testid="btn-no-realizada"
            className="w-full rounded-xl border border-red-500/40 bg-red-500/10 text-red-200 text-sm font-semibold py-3 min-h-[48px]">
            ⛔ No se ha podido realizar (festivo, sin permisos, cerrado…)
          </button>
        )}
        {showNoRealizada && <NoRealizadaSheet rutaId={ruta.id} idx={idx} titulo={p.espacio?.nombre || `#${p.orden}`} onClose={() => setShowNoRealizada(false)}
          onDone={() => { setShowNoRealizada(false); onChange?.(); onBack?.(); }} />}
        {!yaCerrada && (
          <>
            {/* PASO 1: Foto previa obligatoria
                Iter 71 — Si hay observaciones y no las ha leído el montador, ocultar PASO 1 */}
            {(!p.observaciones || p.observaciones_leidas_at) && (
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
              <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5 text-amber-400" /> 1. Foto previa <span className="text-red-400">*</span>
                {fotoPrevia && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 ml-1" />}
              </div>
              {fotoPrevia ? (
                <FotoCard foto={fotoPrevia} onDelete={() => quitarFoto(fotoPrevia)} canDelete={!yaIniciada} />
              ) : fotoPreviaOmitida ? (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-2 text-xs text-amber-200" data-testid="foto-previa-omitida-banner">
                  ⚠️ Sin foto previa · <b>{fotoPreviaOmitida.motivo_label || motivoLabel(fotoPreviaOmitida.motivo)}</b>
                  {fotoPreviaOmitida.detalle && <div className="text-amber-100/80 mt-0.5">«{fotoPreviaOmitida.detalle}»</div>}
                  <div className="text-[10px] text-amber-200/70 mt-0.5">Queda registrado en el reporte para oficina.</div>
                </div>
              ) : (
                <>
                  <UploaderFoto onChange={(f) => subirFoto(f, "previa")} uploading={uploading === "previa"}
                    data-testid="foto-previa-input" label="Hacer foto del espacio ANTES" />
                  {!showOmitirPrevia ? (
                    <button type="button" onClick={() => setShowOmitirPrevia(true)} data-testid="omitir-foto-previa-btn"
                      className="w-full mt-2 text-[11px] text-zinc-500 hover:text-amber-300 underline">
                      No puedo hacer la foto previa
                    </button>
                  ) : (
                    <div className="mt-2 border border-amber-500/40 bg-amber-500/5 rounded-lg p-2 space-y-2" data-testid="omitir-foto-previa-panel">
                      <div className="text-xs font-semibold text-amber-200">¿Por qué no puedes hacer la foto previa? <span className="text-red-400">*</span></div>
                      <MotivoEvidenciaPicker motivo={omitirMotivo} detalle={omitirDetalle} onMotivo={setOmitirMotivo} onDetalle={setOmitirDetalle} testPrefix="omitir-motivo" />
                      <div className="flex gap-2">
                        <Button variant="ghost" onClick={() => setShowOmitirPrevia(false)} className="flex-1 text-zinc-400 h-9">Cancelar</Button>
                        <Button onClick={omitirFotoPrevia} disabled={saving} data-testid="omitir-foto-previa-confirmar"
                          className="flex-1 h-9 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Continuar sin foto previa</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {(fotoPrevia || fotoPreviaOmitida) && !yaIniciada && (
                <Button onClick={iniciar} disabled={saving} data-testid="iniciar-montaje-btn"
                  className="w-full mt-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                  Comenzar montaje
                </Button>
              )}
            </div>
            )}

            {yaIniciada && (
              <>
                {/* PASO 1.5 — Estado del montaje (preguntamos ANTES de fotos) */}
                {!estadoCierre && (
                  <div className="bg-amber-500/5 border border-amber-500/40 rounded-xl p-3" data-testid="estado-early">
                    <div className="text-xs font-semibold mb-2 text-amber-300">
                      ¿Cómo va el montaje? <span className="text-zinc-500 font-normal">(podrás cambiar más tarde)</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { k: "montado", label: "✅ Va bien" },
                        { k: "incidencia", label: "⚠️ Incidencia" },
                        { k: "semi_montado", label: "🟡 Semi-montado" },
                        { k: "no_montado", label: "❌ No se monta" },
                      ].map((opt) => (
                        <button key={opt.k}
                          onClick={() => setEstadoCierre(opt.k)}
                          data-testid={`estado-early-${opt.k}`}
                          className="py-2.5 px-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-xs font-semibold text-zinc-200">
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {estadoCierre && (
                  <div className="bg-zinc-900/60 border border-emerald-500/30 rounded-xl p-3 flex items-center justify-between gap-2"
                       data-testid="estado-early-set">
                    <div className="text-xs">
                      Estado actual: <b className="text-emerald-300">
                        {estadoCierre === "montado" ? "✅ Va bien" :
                         estadoCierre === "incidencia" ? "⚠️ Incidencia" :
                         estadoCierre === "semi_montado" ? "🟡 Semi-montado" :
                         estadoCierre === "no_montado" ? "❌ No se monta" : estadoCierre}
                      </b>
                    </div>
                    <button onClick={() => setEstadoCierre("")} className="text-[10px] text-zinc-500 hover:text-amber-300 underline">cambiar</button>
                  </div>
                )}

                {/* PASO 2: Fotos finales por categoría */}
                <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
                  <div className="text-xs font-semibold mb-2 flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5 text-amber-400" /> 2. Fotos del montaje
                    <span className="text-[10px] text-zinc-500 ml-auto">{fotosFinales.length} / recomendado 3 (mín 1 con motivo)</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    {["izquierda", "centro", "derecha", "panoramica"].map((cat) => (
                      <UploaderFoto key={cat} categoria={cat} onChange={(f) => subirFoto(f, "final", cat)}
                        uploading={uploading === "final"}
                        data-testid={`foto-${cat}-btn`}
                        label={cat[0].toUpperCase() + cat.slice(1)}
                        compact existing={fotosFinales.some((f) => f.categoria === cat)} />
                    ))}
                  </div>
                  <UploaderFoto onChange={(f) => subirFoto(f, "final", "libre")} uploading={uploading === "final"}
                    data-testid="foto-libre-btn" label="+ Foto adicional" compact />

                  {/* SUBIDA MÚLTIPLE: elige varias fotos de la galería de golpe */}
                  <label className={`flex flex-row items-center justify-center gap-2 h-11 px-3 mt-2 border-2 border-dashed rounded-lg cursor-pointer text-xs ${
                    uploading === "final" ? "border-amber-500/40 text-amber-300 bg-amber-500/5" : "border-blue-500/40 hover:border-blue-500/70 text-blue-300 hover:text-blue-200 bg-blue-500/5"
                  }`}>
                    {uploading === "final" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImgIcon className="w-4 h-4" />}
                    <span>{uploading === "final" ? "Subiendo varias…" : "📷 Elegir VARIAS fotos de la galería"}</span>
                    <input type="file" accept="image/*" multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        e.target.value = "";
                        if (files.length > 0) subirFotosMultiples(files);
                      }}
                      className="hidden" data-testid="foto-multi-input" />
                  </label>

                  {/* SUBIR VÍDEO (mp4/mov/webm, máx 50 MB) — iter 67 */}
                  <label className={`flex flex-row items-center justify-center gap-2 h-11 px-3 mt-2 border-2 border-dashed rounded-lg cursor-pointer text-xs ${
                    uploading === "final-video" ? "border-amber-500/40 text-amber-300 bg-amber-500/5" : "border-violet-500/40 hover:border-violet-500/70 text-violet-300 hover:text-violet-200 bg-violet-500/5"
                  }`}>
                    {uploading === "final-video" ? <Loader2 className="w-4 h-4 animate-spin" /> : <VideoIcon className="w-4 h-4" />}
                    <span>{uploading === "final-video" ? "Subiendo vídeo…" : "🎥 Subir VÍDEO (máx 50 MB)"}</span>
                    <input type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v"
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) subirVideo(f, "final", "libre"); }}
                      className="hidden" data-testid="video-final-input" />
                  </label>

                  {fotosFinales.length > 0 && (
                    <div className="grid grid-cols-3 gap-1.5 mt-3" data-testid="fotos-finales-grid">
                      {fotosFinales.map((f) => <FotoCard key={f.id} foto={f} onDelete={() => quitarFoto(f)} canDelete mini />)}
                    </div>
                  )}
                </div>

                {/* PASO 3: Cerrar */}
                <Button onClick={() => setShowCierre(true)} data-testid="abrir-cierre-btn"
                  className="w-full bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold py-3 text-base">
                  Cerrar montaje y enviar reporte
                </Button>
              </>
            )}
          </>
        )}

        {/* MODAL DE CIERRE */}
        {showCierre && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => !saving && setShowCierre(false)}>
            <div className="bg-zinc-950 border border-zinc-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 space-y-3">
                <div className="font-bold text-base">¿Cómo termina el montaje?</div>
                <div className="grid grid-cols-2 gap-2" data-testid="estado-cierre-options">
                  {[
                    { k: "montado",     label: "✅ Montado",        color: "emerald" },
                    { k: "semi_montado",label: "⚠️ Semi montado",  color: "amber" },
                    { k: "incidencia",  label: "❌ Incidencia",      color: "red" },
                    { k: "cancelado",   label: "⛔ Cancelado",       color: "zinc" },
                  ].map((opt) => (
                    <button key={opt.k} onClick={() => setEstadoCierre(opt.k)}
                      data-testid={`cierre-${opt.k}`}
                      className={`py-3 rounded-lg border text-sm transition-colors ${
                        estadoCierre === opt.k ? `bg-${opt.color}-500/25 text-${opt.color}-100 border-${opt.color}-500/60 font-semibold` : "bg-zinc-900 border-zinc-800 text-zinc-400"
                      }`}>{opt.label}</button>
                  ))}
                </div>

                {["semi_montado", "incidencia"].includes(estadoCierre) && (
                  <>
                    <div className="text-xs font-semibold mt-2">Tipo de incidencia</div>
                    <div className="grid grid-cols-3 gap-1.5" data-testid="tipo-incidencia-options">
                      {[
                        { k: "tiempo", label: "⏰ Tiempo" },
                        { k: "medida", label: "📐 Medida" },
                        { k: "otros",  label: "❓ Otros" },
                      ].map((opt) => (
                        <button key={opt.k} onClick={() => setTipoInc(opt.k)}
                          data-testid={`tipo-inc-${opt.k}`}
                          className={`py-2 rounded border text-xs transition-colors ${
                            tipoInc === opt.k ? "bg-amber-500/20 text-amber-100 border-amber-500/60 font-semibold" : "bg-zinc-900 border-zinc-800 text-zinc-400"
                          }`}>{opt.label}</button>
                      ))}
                    </div>
                    {tipoInc === "medida" && (
                      <div className="text-[10px] bg-orange-500/15 border border-orange-500/30 rounded p-2 text-orange-200">
                        ⚠️ El material volverá a producción/AAFF para rehacer. La 2ª visita se programará cuando esté listo.
                      </div>
                    )}
                  </>
                )}

                {/* Campo de observaciones SIEMPRE visible:
                    - En montado: opcional, "Observaciones (opcional)"
                    - En incidencia/semi/cancelado: obligatorio, descripción
                */}
                {estadoCierre && (
                  <div>
                    <div className="text-xs font-semibold mb-1">
                      {estadoCierre === "cancelado" ? "¿Por qué se canceló?" :
                       ["semi_montado", "incidencia"].includes(estadoCierre) ? "Descripción de la incidencia *" :
                       "Observaciones (opcional)"}
                    </div>
                    <Textarea rows={3} value={descInc} onChange={(e) => setDescInc(e.target.value)}
                      data-testid="incidencia-desc"
                      placeholder={
                        estadoCierre === "montado"
                          ? "¿Algo que comunicar a la oficina o cliente? (Ej: cliente pide otra fecha, material extra usado, anécdota…)"
                          : "Qué ha pasado, qué falta…"
                      }
                      className="bg-zinc-900 border-zinc-800 text-sm" />
                  </div>
                )}

                {/* Iter 84.6 — Persona contactada in-situ (OBLIGATORIO en incidencia).
                    Imprescindible para rastrear quién autorizó cambios en el PDV. */}
                {estadoCierre === "incidencia" && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
                    <div className="text-xs font-semibold text-amber-200 flex items-center gap-1.5">
                      👤 ¿Con quién has hablado? <span className="text-red-400">*</span>
                    </div>
                    <input value={contactoNombre} onChange={(e) => setContactoNombre(e.target.value)}
                      data-testid="incidencia-contacto-nombre"
                      placeholder="Nombre y apellidos de la persona del cliente / PDV"
                      className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                    <input value={contactoCargo} onChange={(e) => setContactoCargo(e.target.value)}
                      data-testid="incidencia-contacto-cargo"
                      placeholder="Cargo (Encargada, Jefa de tienda, Visual…) — opcional pero recomendado"
                      className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                    <div className="text-[10px] text-amber-200/70">Obligatorio para incidencias. Aparece en el PDF que se manda al cliente.</div>
                  </div>
                )}

                {estadoCierre && estadoCierre !== "cancelado" && (
                  <div>
                    <div className="text-xs font-semibold mb-1">Nombre del cliente (firma)</div>
                    <input value={firmaNombre} onChange={(e) => setFirmaNombre(e.target.value)}
                      data-testid="firma-input"
                      placeholder="Nombre de quien recibe el montaje"
                      className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm" />
                    <div className="text-[10px] text-zinc-500 mt-0.5">Opcional pero recomendado</div>
                  </div>
                )}

                {/* Fase B · menos de 3 fotos finales: cierre sólo con «Evidencia incompleta» + motivo */}
                {["montado", "semi_montado"].includes(estadoCierre) && fotosFinales.length < 3 && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="evidencia-incompleta-block">
                    <div className="text-xs text-amber-200">
                      📷 Tienes <b>{fotosFinales.length}</b> foto{fotosFinales.length === 1 ? "" : "s"} final{fotosFinales.length === 1 ? "" : "es"} (recomendado: 3).
                      {fotosFinales.length === 0 && <span className="block text-red-300 mt-0.5">Hace falta al menos 1 foto final.</span>}
                    </div>
                    {fotosFinales.length > 0 && (
                      <>
                        <label className="flex items-center gap-2 text-xs font-semibold text-amber-100 cursor-pointer">
                          <input type="checkbox" checked={evidenciaIncompleta} onChange={(e) => setEvidenciaIncompleta(e.target.checked)}
                            data-testid="evidencia-incompleta-check" className="accent-amber-500 w-4 h-4" />
                          Cerrar con evidencia incompleta (motivo obligatorio)
                        </label>
                        {evidenciaIncompleta && (
                          <MotivoEvidenciaPicker motivo={eviMotivo} detalle={eviDetalle} onMotivo={setEviMotivo} onDetalle={setEviDetalle} testPrefix="evi-motivo" />
                        )}
                        <div className="text-[10px] text-amber-200/70">No necesita aprobación, pero el reporte quedará marcado como «Evidencia incompleta».</div>
                      </>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button variant="ghost" onClick={() => setShowCierre(false)} className="flex-1 text-zinc-400">Cancelar</Button>
                  <Button onClick={cerrar} disabled={saving || !estadoCierre} data-testid="confirmar-cierre-btn"
                    className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
                    {saving ? "Enviando..." : "Enviar reporte"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </MobileShell>
  );
}

// Iter 125 · Vista simplificada para tareas logísticas internas (proveedor, taller…)
// Sin fotos obligatorias ni reporte. Solo "Hecho" con notas + foto opcional.
function TareaInternaView({ ruta, idx, p, onBack, onChange }) {
  const [notas, setNotas] = useState("");
  const [foto, setFoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const dir = [p.manual_direccion, p.manual_ciudad, p.manual_provincia].filter(Boolean).join(", ");
  const maps = dir ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dir)}` : null;
  const yaCerrada = !!p.estado_cierre;

  const uploadFoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("categoria", "libre");
      const r = await api.post(`/rutas/${ruta.id}/paradas/${idx}/fotos-finales`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFoto(r.data);
      toast.success("Foto subida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo foto");
    } finally { setUploading(false); }
  };

  const marcarHecho = async () => {
    setSaving(true);
    try {
      await api.post(`/rutas/${ruta.id}/paradas/${idx}/cerrar`, {
        estado_cierre: "montado",   // reusamos 'montado' como "hecho"
        incidencia_descripcion: notas.trim() || null,
      });
      toast.success("Tarea marcada como hecha");
      onChange && (await onChange());
      onBack();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-cyan-500/10 border border-cyan-500/40 rounded-xl p-3 flex items-start gap-2"
           data-testid="tarea-interna-banner">
        <span className="text-2xl">🔧</span>
        <div>
          <div className="text-sm font-semibold text-cyan-100">Tarea logística interna</div>
          <div className="text-xs text-cyan-200/80 leading-snug mt-0.5">
            Sin reporte ni albarán. Solo pulsa Hecho cuando termines. La foto es opcional.
          </div>
        </div>
      </div>

      <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
        <div className="text-lg font-semibold text-zinc-100">{p.manual_titulo || "Tarea"}</div>
        {dir && <div className="text-xs text-zinc-400 mt-1"><MapPin className="w-3 h-3 inline" /> {dir}</div>}
        {maps && (
          <a href={maps} target="_blank" rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-zinc-900 font-semibold text-sm px-3 py-2 rounded-lg w-full justify-center">
            <ExternalLink className="w-4 h-4" /> Llévame con Google Maps
          </a>
        )}
        {p.manual_contacto && (
          <div className="mt-2 pt-2 border-t border-zinc-800 text-sm">
            <div className="text-zinc-500 text-xs">Contacto</div>
            <div>{p.manual_contacto}</div>
            {p.manual_telefono && (
              <a href={`tel:${p.manual_telefono}`} className="inline-flex items-center gap-1 text-cyan-400 mt-1 text-sm">
                <Phone className="w-3.5 h-3.5" /> {p.manual_telefono}
              </a>
            )}
          </div>
        )}
        {p.observaciones && (
          <div className="mt-2 pt-2 border-t border-zinc-800 text-xs">
            <div className="text-zinc-500 mb-1">Observaciones</div>
            <div className="text-zinc-300 whitespace-pre-wrap">{p.observaciones}</div>
          </div>
        )}
      </div>

      {yaCerrada ? (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4 text-center text-emerald-200 text-sm font-semibold">
          ✅ Tarea completada
        </div>
      ) : (
        <>
          <div>
            <div className="text-xs text-zinc-400 mb-1">Notas (opcional)</div>
            <textarea
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              placeholder="Ej: recogido material X, entregado albarán del proveedor…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm mt-1"
              rows={3}
              maxLength={500}
              data-testid="tarea-interna-notas"
            />
          </div>

          {/* Foto opcional de justificación (factura proveedor, ticket, coche en taller…) */}
          {!foto ? (
            <label className="flex items-center justify-center gap-2 h-14 border-2 border-dashed border-zinc-700 hover:border-cyan-500/50 rounded-lg cursor-pointer text-sm text-zinc-400 hover:text-cyan-300"
                   data-testid="tarea-interna-foto-uploader">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              <span>{uploading ? "Subiendo…" : "Foto de justificación (opcional)"}</span>
              <input type="file" accept="image/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadFoto(e.target.files[0])} />
            </label>
          ) : (
            <div className="relative bg-zinc-900 rounded-lg overflow-hidden border border-emerald-500/40">
              <img src={foto.url} alt="Justificación" className="w-full max-h-56 object-cover" />
              <div className="absolute top-1 left-1 bg-emerald-500/80 text-zinc-900 text-[10px] px-1.5 py-0.5 rounded font-semibold">
                ✓ Subida
              </div>
            </div>
          )}

          <button
            onClick={marcarHecho}
            disabled={saving}
            data-testid="tarea-interna-hecho-btn"
            className="w-full mt-2 bg-emerald-500 hover:bg-emerald-600 text-zinc-900 font-bold text-lg rounded-xl py-4 flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
            {saving ? "Guardando…" : "✅ Hecho"}
          </button>
        </>
      )}
    </div>
  );
}

function FotoCard({ foto, onDelete, canDelete, mini }) {
  const isVideo = foto?.tipo === "video" || /\.(mp4|mov|webm|m4v)(\?|$)/i.test(foto?.url || "");
  return (
    <div className={`relative bg-zinc-950 rounded overflow-hidden border border-zinc-800 ${mini ? "" : "max-w-md"}`}>
      {isVideo ? (
        <video
          src={foto.url}
          poster={foto.thumbnail_url || undefined}
          controls
          preload="metadata"
          playsInline
          className={`w-full ${mini ? "h-20" : "max-h-56"} object-cover bg-zinc-900`}
        />
      ) : (
        <a href={foto.url} target="_blank" rel="noreferrer">
          <img src={cloudinaryThumb(foto.url, mini ? 200 : 400, mini ? 130 : 240)} alt={foto.categoria}
            className={`w-full ${mini ? "h-20" : "max-h-56"} object-cover`} />
        </a>
      )}
      <div className="absolute top-0.5 left-0.5 bg-black/70 rounded px-1 text-[9px] uppercase flex items-center gap-1">
        {isVideo && <Play className="w-2.5 h-2.5" />}
        {isVideo ? "vídeo" : foto.categoria}
      </div>
      {foto.latitud && (
        <div className="absolute bottom-0.5 left-0.5 bg-emerald-500/80 text-zinc-900 text-[9px] px-1 rounded">📍 GPS</div>
      )}
      {canDelete && (
        <button onClick={onDelete}
          className="absolute top-1 right-1 bg-red-500/80 rounded-full w-5 h-5 flex items-center justify-center text-white text-xs">×</button>
      )}
    </div>
  );
}

// Iter 124 · Tarjeta detallada de componente para el montador.
// Muestra render/foto, medidas, material, instrucciones especiales, sub-piezas
// (si es marco_vinilo) y archivos AAFF (fotos/renders subidos por diseño).
function ComponenteMontajeCard({ c, paradaComponentes = [] }) {
  const [expandido, setExpandido] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const codigo = String(c.id || "").slice(0, 6).toUpperCase();
  const thumb = componenteImgSrc(c, 240, 240);
  const b64Thumb = null;
  const archivos = c.archivos_aaff || [];
  const subs = paradaComponentes.filter((x) => x.parent_id === c.id);
  const esMarco = c.tipo_plantilla === "marco_vinilo";
  const mp = c.marco_params || {};
  const tieneExtras = c.descripcion || c.descripcion_encargo || c.notas || c.observaciones_taller ||
                       archivos.length > 0 || subs.length > 0 || esMarco;
  const previewImg = thumb || b64Thumb;
  return (
    <div className="bg-zinc-950/60 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`comp-card-${c.id}`}>
      <div className="flex gap-2 p-2">
        {previewImg && (
          <a href={previewImg} target="_blank" rel="noreferrer"
             className="shrink-0 w-14 h-14 rounded overflow-hidden bg-zinc-900 border border-zinc-700"
             data-testid={`comp-thumb-${c.id}`}>
            <img src={previewImg} alt={c.nombre} className="w-full h-full object-cover" />
          </a>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-[10px] bg-emerald-500/20 text-emerald-200 rounded px-1">{codigo}</span>
            {c.bulto_numero && <span className="font-mono text-[10px] bg-emerald-500/15 text-emerald-300 rounded px-1">📦{c.bulto_numero}</span>}
            {esMarco && <span className="text-[10px] bg-violet-500/20 text-violet-300 rounded px-1">📐 marco vinilo</span>}
            {c.cantidad > 1 && <span className="text-[10px] font-bold text-amber-300">×{c.cantidad}</span>}
          </div>
          <div className="text-sm font-semibold text-zinc-100 leading-tight">{c.nombre}</div>
          <div className="text-[11px] text-zinc-400 mt-0.5 flex flex-wrap gap-x-2">
            {c.ancho_cm && c.alto_cm && <span>{c.ancho_cm}×{c.alto_cm} cm</span>}
            {c.metros_cuadrados && <span>{c.metros_cuadrados} m²</span>}
            {c.tamano && !c.ancho_cm && <span>{c.tamano}</span>}
          </div>
        </div>
        {tieneExtras && (
          <button onClick={() => setExpandido((v) => !v)}
            className="shrink-0 text-zinc-400 hover:text-amber-300 text-[11px] px-1"
            data-testid={`comp-toggle-${c.id}`}>
            {expandido ? "▲" : "▼"}
          </button>
        )}
      </div>

      {/* Descripcion_encargo siempre visible (instrucción crítica del pedido) */}
      {c.descripcion_encargo && (
        <div className="bg-amber-500/10 border-t border-amber-500/30 px-2 py-1.5 text-[11px] text-amber-200 leading-snug"
             data-testid={`comp-encargo-${c.id}`}>
          ⚠️ <b>Encargo:</b> {c.descripcion_encargo}
        </div>
      )}

      {expandido && (
        <div className="border-t border-zinc-800 p-2 space-y-2 text-[11px]">
          {c.descripcion && (
            <div>
              <div className="text-zinc-500 uppercase text-[9px]">Descripción</div>
              <div className="text-zinc-300 whitespace-pre-wrap">{c.descripcion}</div>
            </div>
          )}
          {c.notas && (
            <div>
              <div className="text-zinc-500 uppercase text-[9px]">Notas</div>
              <div className="text-zinc-300 whitespace-pre-wrap">{c.notas}</div>
            </div>
          )}
          {c.observaciones_taller && (
            <div>
              <div className="text-zinc-500 uppercase text-[9px]">Obs. taller</div>
              <div className="text-zinc-300 whitespace-pre-wrap">{c.observaciones_taller}</div>
            </div>
          )}
          {esMarco && (mp.ancho_escaparate || mp.alto_escaparate) && (
            <div className="bg-violet-500/5 border border-violet-500/30 rounded p-1.5">
              <div className="text-[9px] uppercase text-violet-300 mb-0.5">Marco vinilo</div>
              <div className="text-zinc-300">
                Escaparate {mp.ancho_escaparate}×{mp.alto_escaparate} cm ·
                Cab. {mp.ancho_cabecera ?? 25}cm · Pie {mp.ancho_pie ?? 15}cm · Lat. {mp.ancho_lateral ?? 10}cm
              </div>
            </div>
          )}
          {subs.length > 0 && (
            <div>
              <div className="text-zinc-500 uppercase text-[9px] mb-1">Sub-piezas ({subs.length})</div>
              <ul className="space-y-0.5">
                {subs.map((s) => (
                  <li key={s.id} className="text-zinc-300 flex justify-between gap-2">
                    <span className="truncate">· {s.nombre}</span>
                    <span className="text-zinc-500 shrink-0 font-mono">
                      {s.ancho_cm}×{s.alto_cm}cm
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {archivos.length > 0 && (
            <div>
              <button
                onClick={() => setShowFiles((v) => !v)}
                data-testid={`comp-toggle-files-${c.id}`}
                className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">
                📎 Renders / archivos AAFF ({archivos.length}) {showFiles ? "▲" : "▼"}
              </button>
              {showFiles && (
                <div className="mt-1.5 grid grid-cols-3 gap-1.5" data-testid={`comp-files-${c.id}`}>
                  {archivos.map((a) => {
                    const isImg = (a.mime || a.tipo || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(a.url || "");
                    return (
                      <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                         className="bg-zinc-900 border border-zinc-800 rounded overflow-hidden hover:border-cyan-500/50">
                        {isImg ? (
                          <img src={cloudinaryThumb(a.url, 200, 150)} alt={a.nombre}
                               className="w-full h-16 object-cover" />
                        ) : (
                          <div className="w-full h-16 flex items-center justify-center text-cyan-300">
                            <FileText className="w-6 h-6" />
                          </div>
                        )}
                        <div className="text-[9px] p-0.5 truncate text-zinc-400">{a.nombre}</div>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function UploaderFoto({ onChange, uploading, label, compact, existing, categoria, "data-testid": testId }) {
  return (
    <label className={`flex ${compact ? "flex-row" : "flex-col"} items-center justify-center gap-2 ${compact ? "h-11 px-3" : "w-full h-32"} border-2 border-dashed rounded-lg cursor-pointer text-xs ${
      existing ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" : "border-zinc-800 hover:border-amber-500/50 text-zinc-400 hover:text-amber-300"
    }`}>
      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : existing ? <CheckCircle2 className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
      <span>{existing ? `${label} ✓` : uploading ? "Subiendo…" : label}</span>
      {/* Sin `capture="environment"`: el navegador móvil mostrará el menú nativo
          (Cámara / Fotos / Archivos), permitiendo elegir de la galería. */}
      <input type="file" accept="image/*"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onChange(f); }}
        className="hidden" data-testid={testId} />
    </label>
  );
}

// Iter 71 — Observaciones de la parada con check obligatorio de lectura
function ObservacionesBanner({ parada, rutaId, idx, onAck }) {
  const [acking, setAcking] = useState(false);
  const yaLeido = !!parada.observaciones_leidas_at;
  const ack = async () => {
    setAcking(true);
    try {
      await api.post(`/rutas/${rutaId}/paradas/${idx}/ack-observaciones`);
      toast.success("Observaciones marcadas como leídas");
      if (typeof onAck === "function") {
        try { await onAck(); } catch { /* ignored */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando confirmación");
    } finally {
      setAcking(false);
    }
  };
  return (
    <div className={`rounded-xl border-2 p-3 ${yaLeido
      ? "bg-emerald-500/5 border-emerald-500/30"
      : "bg-amber-500/15 border-amber-500/60 shadow-lg shadow-amber-500/10"}`}>
      <div className={`text-[11px] uppercase tracking-wider mb-1.5 flex items-center gap-1.5 font-bold ${yaLeido ? "text-emerald-300" : "text-amber-200"}`}>
        ⚠️ Instrucciones importantes
        {yaLeido && <CheckCircle2 className="w-3.5 h-3.5" />}
      </div>
      <div className="whitespace-pre-wrap text-sm text-zinc-100 mb-2">{parada.observaciones}</div>
      {yaLeido ? (
        <div className="text-[10px] text-emerald-300 font-mono">
          ✓ Leído por {parada.observaciones_leidas_por || "—"} ·{" "}
          {parada.observaciones_leidas_at
            ? new Date(parada.observaciones_leidas_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })
            : ""}
        </div>
      ) : (
        <button
          onClick={ack}
          disabled={acking}
          data-testid="ack-observaciones-btn"
          className="w-full px-3 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
          {acking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
          He leído estas observaciones
        </button>
      )}
    </div>
  );
}

// Nota: ObservacionesBanner usa `onChange` que viene del closure de PartParada (no expuesto como prop).
// Se inyecta como prop `onAck` para mantener pureza; ambas formas son válidas.

